import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth, storage } from '../firebase';
import { updatePassword, verifyBeforeUpdateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  User, Building2, CreditCard, Plug, Save, LogOut, X,
  Eye, EyeOff, Mail, CheckCircle2, Camera, Trash2, KeyRound,
} from 'lucide-react';
import { isInternalUser } from '../services/accessControlService';
import { updateAccount, findAccountIdByEmail, getCompanyIdForUser } from '../services/firestoreService';
import JobTreadTest from '../components/JobTreadTest';
import { 
  getUserIntegrationsStatus,
  initiateUserIntegrationConnection,
  disconnectUserIntegration,
  saveJobTreadGrant,
  getIntegrationDisplayName as getUserIntegrationDisplayName,
  isIntegrationConfigured as isUserIntegrationConfigured
} from '../services/userIntegrationService';
import {
  initiateIntegrationConnection,
  getAccountIntegrationsStatus,
  disconnectAccountIntegrationConnection,
  isIntegrationConfigured,
  getIntegrationDisplayName,
  initiateAccountIntegrationConnection,
} from '../services/integrationService';
import {
  connectFathomWithApiKey,
  disconnectFathom,
} from '../services/fathomIntegrationService';
import { 
  saveUserNotificationPreferencesForCollection,
  getUserNotificationPreferences,
  getNotificationDisplayName
} from '../services/notificationService';
import PortalBackground from '../components/PortalBackground';

// Shared visual styles (design language)
const inputBase = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';
const labelBase = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
const sectionCard = 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-6';

const SectionHeading = ({ emoji, children }) => (
  <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
    <span className="text-lg leading-none">{emoji}</span>
    {children}
  </h2>
);

const Toggle = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
      checked ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[22px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const NOTIFICATION_TILES = [
  { key: 'newMessage', label: 'New Message', emoji: '📩', tile: 'bg-purple-50 dark:bg-purple-900/30' },
  { key: 'communityPost', label: 'Community Post', emoji: '💬', tile: 'bg-emerald-50 dark:bg-emerald-900/30' },
  { key: 'generalFulfillment', label: 'General Update', emoji: '📋', tile: 'bg-slate-100 dark:bg-slate-800' },
  { key: 'technologyFulfillment', label: 'Technology Update', emoji: '🤖', tile: 'bg-cyan-50 dark:bg-cyan-900/30' },
  { key: 'hrFulfillment', label: 'HR Update', emoji: '👥', tile: 'bg-pink-50 dark:bg-pink-900/30' },
  { key: 'marketingFulfillment', label: 'Marketing Update', emoji: '📣', tile: 'bg-teal-50 dark:bg-teal-900/30' },
  { key: 'newAnnouncement', label: 'New Announcement', emoji: '📢', tile: 'bg-orange-50 dark:bg-orange-900/30' },
  { key: 'newQuestion', label: 'Question Update', emoji: '❓', tile: 'bg-amber-50 dark:bg-amber-900/30' },
  { key: 'newFire', label: 'Fire Update', emoji: '🔥', tile: 'bg-red-50 dark:bg-red-900/30' },
  { key: 'newTask', label: 'New Task', emoji: '✅', tile: 'bg-indigo-50 dark:bg-indigo-900/30' },
  { key: 'completedTask', label: 'Completed Task', emoji: '🎉', tile: 'bg-green-50 dark:bg-green-900/30' },
  { key: 'newEvent', label: 'New Event', emoji: '📅', tile: 'bg-lime-50 dark:bg-lime-900/30' },
];

const MyProfile = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDev = process.env.NODE_ENV === 'development';
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showJobTreadModal, setShowJobTreadModal] = useState(false);
  const [jobTreadGrantKey, setJobTreadGrantKey] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [emailChangeData, setEmailChangeData] = useState({ newEmail: '', password: '' });
  const [emailPasswordVisible, setEmailPasswordVisible] = useState(false);
  const [emailModalError, setEmailModalError] = useState('');
  const [accountId, setAccountId] = useState(null);
  const fileInputRef = useRef(null);
  const companyLogoInputRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    companyColors: {
      primary: '#dc2626',
      secondary: '#1f2937'
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    receiveEmails: true
  });

  const [notifications, setNotifications] = useState({
    newMessage: false,
    communityPost: false,
    generalFulfillment: false,
    technologyFulfillment: false,
    hrFulfillment: false,
    marketingFulfillment: false,
    newAnnouncement: false,
    newQuestion: false,
    newFire: false,
    newTask: false,
    completedTask: false,
    newEvent: false
  });

  // State for internal user features
  const [isInternal, setIsInternal] = useState(false);
  
  // Integration states
  const [integrationsStatus, setIntegrationsStatus] = useState({});
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [connectingIntegration, setConnectingIntegration] = useState(null);
  const [showFathomKeyModal, setShowFathomKeyModal] = useState(false);
  const [fathomApiKeyDraft, setFathomApiKeyDraft] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        const internalStatus = await isInternalUser(currentUser.uid);
        setIsInternal(internalStatus);

        // Load profile data from appropriate collection
        let userData = {};
        if (internalStatus) {
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            userData = internalUserDoc.data();
          }
        } else {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            userData = userDoc.data();
          }
        }

        if (Object.keys(userData).length > 0) {
          let accId = null;
          if (!internalStatus) {
            accId = userData.accountId || userData.clientId || null;
            if (!accId) {
              // Fallback: resolve company by email if mapping missing
              const resolved = await findAccountIdByEmail(userData.email || currentUser.email || '', 'Clients').catch(() => null);
              if (resolved) {
                accId = resolved;
              }
            }
            if (accId) {
              setAccountId(accId);
            }
          } else {
            setAccountId(null);
          }

          let accountData = {};
          if (accId) {
            try {
              const accountDoc = await getDoc(doc(db, 'accounts', accId));
              if (accountDoc.exists()) {
                accountData = accountDoc.data();
              }
            } catch (e) {
              console.warn('Could not load account data for profile:', e);
            }
          }

          const authEmail = userData.email || currentUser.email || '';
          setProfileData({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: authEmail,
            phone: userData.phone || '',
            company: accountData.company || userData.company || '',
            website: accountData.website || userData.website || '',
            companyColors: accountData.companyColors || userData.companyColors || {
              primary: '#dc2626',
              secondary: '#1f2937'
            },
            address: {
              street: accountData.address?.street || userData.address?.street || '',
              city: accountData.address?.city || userData.address?.city || '',
              state: accountData.address?.state || userData.address?.state || '',
              zipCode: accountData.address?.zipCode || userData.address?.zipCode || '',
              country: accountData.address?.country || userData.address?.country || 'United States'
            },
            receiveEmails: userData.receiveEmails !== undefined ? userData.receiveEmails : true
          });

          // Portal-visible company logo only (never crmCompanyLogo — that is CRM-internal)
          const portalVisibleLogo = userData.companyLogo || accountData.companyLogo || null;
          if (portalVisibleLogo) {
            setCompanyLogo(portalVisibleLogo);
          }

          // Load team members (other users with same accountId/clientId, excluding current user)
          if (!internalStatus && accId) {
            try {
              const usersRef = collection(db, 'users');
              const accountQuery = query(usersRef, where('accountId', '==', accId));
              const clientQuery = query(usersRef, where('clientId', '==', accId));
              const [accountSnap, clientSnap] = await Promise.all([
                getDocs(accountQuery),
                getDocs(clientQuery)
              ]);
              const docMap = new Map();
              [...accountSnap.docs, ...clientSnap.docs].forEach(d => docMap.set(d.id, d));
              const teammates = Array.from(docMap.values())
                .filter(d => d.id !== currentUser.uid)
                .map(d => {
                  const data = d.data();
                  return {
                    id: d.id,
                    name: [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || 'Unknown'
                  };
                });
              setTeamMembers(teammates);
            } catch (teamErr) {
              console.warn('Could not load team members:', teamErr);
              setTeamMembers([]);
            }
          }
          if (currentUser.email && userData.email !== currentUser.email) {
            try {
              const collectionName = internalStatus ? 'internal_users' : 'users';
              const userRef = doc(db, collectionName, currentUser.uid);
              await updateDoc(userRef, { email: currentUser.email, lastUpdated: new Date().toISOString() });
              const accountIdFromUser = userData.accountId || userData.clientId;
              if (accountIdFromUser) {
                await updateAccount(accountIdFromUser, {
                  contact: { ...(userData.contact || {}), email: currentUser.email },
                  email: currentUser.email
                });
              }
            } catch (syncErr) {
              console.error('Error syncing email to profile/account:', syncErr);
            }
          }
          
          if (userData.profileImage) {
            setProfileImage(userData.profileImage);
          }

          // Load notification preferences.
          // newTask is ON by default for portal (client) users — only an explicit
          // false (user toggled it off) shows as disabled. Internal users stay opt-in.
          {
            const prefs = userData.notifications || {};
            setNotifications({
              newMessage: prefs.newMessage || false,
              communityPost: prefs.communityPost || false,
              generalFulfillment: prefs.generalFulfillment || false,
              technologyFulfillment: prefs.technologyFulfillment || false,
              hrFulfillment: prefs.hrFulfillment || false,
              marketingFulfillment: prefs.marketingFulfillment || false,
              newAnnouncement: prefs.newAnnouncement || false,
              newQuestion: prefs.newQuestion || false,
              newFire: prefs.newFire || false,
              newTask: internalStatus ? (prefs.newTask || false) : prefs.newTask !== false,
              completedTask: prefs.completedTask || false,
              newEvent: prefs.newEvent || false
            });
          }
          
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setMessage({ type: 'error', text: 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };

    const fetchIntegrationsStatus = async () => {
      try {
        setIntegrationsLoading(true);
        if (currentUser) {
          const userStatus = await getUserIntegrationsStatus(currentUser.uid);
          let merged = { ...userStatus };
          // Portal clients: merge account-level QuickBooks from internalIntegrations/{accountId}
          const internalDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          const isInternalUser = internalDoc.exists();
          if (!isInternalUser) {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const accId = userDoc.exists() ? (userDoc.data().accountId || userDoc.data().clientId) : null;
            if (accId) {
              try {
                const accountStatus = await getAccountIntegrationsStatus(accId, { forceRefresh: true });
                const acctConnected = accountStatus.connectedIntegrations || accountStatus;
                merged = {
                  ...merged,
                  quickbooks: acctConnected.quickbooks || { connected: false },
                  docusign: acctConnected.docusign || { connected: false },
                  googleanalytics: acctConnected.googleanalytics || { connected: false },
                };
              } catch (e) {
                console.warn('Could not fetch account integrations status:', e);
              }
            }
          }
          setIntegrationsStatus(merged);
        }
      } catch (error) {
        console.error('Error fetching integrations status:', error);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    loadProfile();
    fetchIntegrationsStatus();
  }, [currentUser]);

  // Refetch integrations when returning from OAuth callback
  useEffect(() => {
    if (location.state?.refreshIntegrations && currentUser) {
      const refetch = async () => {
        try {
          const userStatus = await getUserIntegrationsStatus(currentUser.uid);
          let merged = { ...userStatus };
          const internalDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (!internalDoc.exists()) {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const accId = userDoc.exists() ? (userDoc.data().accountId || userDoc.data().clientId) : null;
            if (accId) {
              const accountStatus = await getAccountIntegrationsStatus(accId, { forceRefresh: true });
              const acctConnected = accountStatus.connectedIntegrations || accountStatus;
              merged = {
                ...merged,
                quickbooks: acctConnected.quickbooks || { connected: false },
                docusign: acctConnected.docusign || { connected: false },
                googleanalytics: acctConnected.googleanalytics || { connected: false },
              };
            }
          }
          setIntegrationsStatus(merged);
        } catch (e) {
          console.warn('Error refetching integrations:', e);
        }
        navigate(location.pathname, { replace: true, state: {} });
      };
      refetch();
    }
  }, [location.state?.refreshIntegrations, currentUser, navigate, location.pathname]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setProfileData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone,
        receiveEmails: profileData.receiveEmails,
        darkMode: darkMode,
        lastUpdated: new Date().toISOString()
      };
      
      // Include company logo if it exists
      if (companyLogo) {
        updateData.companyLogo = companyLogo;
      }
      
      // Update the appropriate collection
      const collectionName = isInternal ? 'internal_users' : 'users';
      await updateDoc(doc(db, collectionName, currentUser.uid), updateData);

       // For portal clients, also persist shared company info on the account so all team members see the same data
       const companyId = await getCompanyIdForUser(currentUser.uid);
       if (!isInternal && companyId) {
         const accountUpdate = {
           company: profileData.company,
           website: profileData.website,
           companyColors: profileData.companyColors,
           address: profileData.address
         };
         if (companyLogo) {
           accountUpdate.companyLogo = companyLogo;
         }
         await updateAccount(companyId, accountUpdate);
       }
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select a valid image file.' });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
        return;
      }

      try {
        setSaving(true);
        setMessage({ type: '', text: '' });

        // Upload to Firebase Storage (same approach as CRM profile)
        const storageRef = ref(storage, `profilePictures/${currentUser.uid}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Update user document in the appropriate collection
        const collectionName = isInternal ? 'internal_users' : 'users';
        await updateDoc(doc(db, collectionName, currentUser.uid), {
          profileImage: downloadURL,
          lastUpdated: new Date().toISOString()
        });

        setProfileImage(downloadURL);
        setMessage({ type: 'success', text: 'Profile picture updated successfully!' });

        if (!isInternal) {
          const companyId = await getCompanyIdForUser(currentUser.uid);
          if (companyId) {
            const accSnap = await getDoc(doc(db, 'accounts', companyId));
            if (accSnap.exists()) {
              const acc = accSnap.data();
              const primaryEmail = String(acc.contact?.email || acc.email || '').trim().toLowerCase();
              const userEmail = String(currentUser.email || '').trim().toLowerCase();
              if (primaryEmail && userEmail === primaryEmail) {
                const existingContact = acc.contact || {};
                await updateAccount(companyId, {
                  contact: {
                    ...existingContact,
                    portalPhotoURL: downloadURL
                  }
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteImage = async (e) => {
    e.stopPropagation();
    if (!profileImage) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Delete from storage (same approach as CRM profile)
      const storageRef = ref(storage, `profilePictures/${currentUser.uid}`);
      await deleteObject(storageRef);

      // Update user document in the appropriate collection
      const collectionName = isInternal ? 'internal_users' : 'users';
      await updateDoc(doc(db, collectionName, currentUser.uid), {
        profileImage: null,
        lastUpdated: new Date().toISOString()
      });

      setProfileImage(null);
      setMessage({ type: 'success', text: 'Profile picture removed successfully!' });

      if (!isInternal) {
        const companyId = await getCompanyIdForUser(currentUser.uid);
        if (companyId) {
          const accSnap = await getDoc(doc(db, 'accounts', companyId));
          if (accSnap.exists()) {
            const acc = accSnap.data();
            const primaryEmail = String(acc.contact?.email || acc.email || '').trim().toLowerCase();
            const userEmail = String(currentUser.email || '').trim().toLowerCase();
            if (primaryEmail && userEmail === primaryEmail) {
              const existingContact = acc.contact || {};
              await updateAccount(companyId, {
                contact: {
                  ...existingContact,
                  portalPhotoURL: ''
                }
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage({ type: 'error', text: 'Failed to remove profile picture. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyLogoClick = () => {
    companyLogoInputRef.current?.click();
  };

  const handleCompanyLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select a valid image file.' });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
        return;
      }

      try {
        setSaving(true);
        setMessage({ type: '', text: '' });

        // Upload to Firebase Storage
        const storageRef = ref(storage, `companyLogos/${currentUser.uid}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Update user document in the appropriate collection
        const collectionName = isInternal ? 'internal_users' : 'users';
        await updateDoc(doc(db, collectionName, currentUser.uid), {
          companyLogo: downloadURL,
          lastUpdated: new Date().toISOString()
        });

        setCompanyLogo(downloadURL);

        // Also persist on the company account so all team members share the same logo
        const companyId = await getCompanyIdForUser(currentUser.uid);
        if (!isInternal && companyId) {
          await updateAccount(companyId, { companyLogo: downloadURL });
        }
        setMessage({ type: 'success', text: 'Company logo updated successfully!' });
      } catch (error) {
        console.error('Error uploading company logo:', error);
        setMessage({ type: 'error', text: 'Failed to upload company logo. Please try again.' });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteCompanyLogo = async (e) => {
    e.stopPropagation();
    if (!companyLogo) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Delete from storage
      const storageRef = ref(storage, `companyLogos/${currentUser.uid}`);
      await deleteObject(storageRef);

      // Update user document in the appropriate collection
      const collectionName = isInternal ? 'internal_users' : 'users';
      await updateDoc(doc(db, collectionName, currentUser.uid), {
        companyLogo: null,
        lastUpdated: new Date().toISOString()
      });

      setCompanyLogo(null);

      // Also clear on the company account
      const companyId = await getCompanyIdForUser(currentUser.uid);
      if (!isInternal && companyId) {
        await updateAccount(companyId, { companyLogo: null });
      }
      setMessage({ type: 'success', text: 'Company logo removed successfully!' });
    } catch (error) {
      console.error('Error deleting company logo:', error);
      setMessage({ type: 'error', text: 'Failed to remove company logo. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      setMessage({
        type: 'error',
        text: 'Error signing out. Please try again.'
      });
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const newPassword = passwordData.newPassword;
    const confirmPassword = passwordData.confirmPassword;

    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match.'
      });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 6 characters long.'
      });
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      await updatePassword(user, newPassword);
      setMessage({
        type: 'success',
        text: 'Password updated successfully!'
      });
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
    } catch (error) {
      console.error("Error updating password:", error);
      setMessage({
        type: 'error',
        text: error.message || 'Error updating password. Please try again.'
      });
    }
  };

  const handleEmailChange = async () => {
    const newEmail = (emailChangeData.newEmail || '').trim().toLowerCase();
    const password = emailChangeData.password;

    if (!newEmail) {
      setEmailModalError('Please enter a new email address');
      return;
    }
    if (newEmail === (currentUser?.email || '').toLowerCase()) {
      setEmailModalError('New email is the same as your current email');
      return;
    }
    if (!password) {
      setEmailModalError('Please enter your current password to confirm your identity');
      return;
    }

    try {
      setSaving(true);
      setEmailModalError('');

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      const actionCodeSettings = {
        url: window.location.origin + '/login?emailChanged=1',
        handleCodeInApp: false
      };
      await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

      setMessage({ type: 'success', text: `We've sent a confirmation link to ${newEmail}. Click the link in that email to complete the change. Your email will stay the same until you click it.` });
      setShowEmailModal(false);
      setEmailChangeData({ newEmail: '', password: '' });
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setEmailModalError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        setEmailModalError('This email is already in use by another account.');
      } else if (error.code === 'auth/requires-recent-login') {
        setEmailModalError('Please sign in again, then try changing your email.');
      } else {
        setEmailModalError(error.message || 'Failed to update email');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConnectIntegration = async (integrationKey) => {
    try {
      setConnectingIntegration(integrationKey);
      
      if (integrationKey === 'jobtread') {
        // Show modal for JobTread grant key input
        setShowJobTreadModal(true);
        setConnectingIntegration(null);
        return;
      }

      if (integrationKey === 'fathom') {
        if (!isInternal) {
          setMessage({ type: 'error', text: 'Fathom is available for internal users only.' });
          return;
        }
        setFathomApiKeyDraft('');
        setShowFathomKeyModal(true);
        setConnectingIntegration(null);
        return;
      }
      
      // Account-level OAuth integrations for portal clients
      if (integrationKey === 'quickbooks' || integrationKey === 'docusign' || integrationKey === 'googleanalytics') {
        if (!isIntegrationConfigured(integrationKey)) {
          setMessage({
            type: 'error',
            text: `${getIntegrationDisplayName(integrationKey)} is not configured. Please contact your administrator.`
          });
          return;
        }
        if (!accountId) {
          setMessage({
            type: 'error',
            text: `Account not found. Please contact support to connect ${getIntegrationDisplayName(integrationKey)}.`
          });
          return;
        }
        // googleanalytics embeds accountId in state so tokens are stored per-account
        if (integrationKey === 'googleanalytics') {
          await initiateAccountIntegrationConnection(integrationKey, accountId);
        } else {
          await initiateIntegrationConnection(integrationKey);
        }
        return;
      }
      
      if (!isUserIntegrationConfigured(integrationKey)) {
        setMessage({
          type: 'error',
          text: `${getUserIntegrationDisplayName(integrationKey)} is not configured. Please contact your administrator.`
        });
        return;
      }

      await initiateUserIntegrationConnection(integrationKey);
    } catch (error) {
      console.error('Error connecting integration:', error);
      const isAccountLevelIntegration = integrationKey === 'quickbooks' || integrationKey === 'docusign';
      const displayName = isAccountLevelIntegration ? getIntegrationDisplayName(integrationKey) : getUserIntegrationDisplayName(integrationKey);
      setMessage({
        type: 'error',
        text: `Failed to connect ${displayName}: ${error.message}`
      });
    } finally {
      setConnectingIntegration(null);
    }
  };

  const handleJobTreadSubmit = async () => {
    if (!jobTreadGrantKey || !jobTreadGrantKey.trim()) {
      setMessage({
        type: 'error',
        text: 'Grant key is required for JobTread integration'
      });
      return;
    }

    try {
      setConnectingIntegration('jobtread');
      setShowJobTreadModal(false);
      
      const trimmedGrantKey = jobTreadGrantKey.trim();
      
      // Test the grant key by making a simple API call (pass the grant key directly)
      const { testJobTreadConnection } = await import('../services/jobtreadService');
      await testJobTreadConnection(trimmedGrantKey);
      
      // If successful, store it server-side (also sets integrations.jobtread.connected)
      await saveJobTreadGrant(trimmedGrantKey);
      
      // Refresh integrations status
      const status = await getUserIntegrationsStatus(currentUser.uid);
      setIntegrationsStatus(status);
      
      setJobTreadGrantKey('');
      setMessage({
        type: 'success',
        text: 'JobTread connected successfully!'
      });
    } catch (error) {
      console.error('Error connecting JobTread:', error);
      // Extract a more user-friendly error message
      let errorMessage = 'Failed to connect JobTread';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code) {
        errorMessage = `Failed to connect JobTread: ${error.code}`;
      }
      setMessage({
        type: 'error',
        text: errorMessage
      });
      // Re-open the modal so user can try again
      setShowJobTreadModal(true);
    } finally {
      setConnectingIntegration(null);
    }
  };

  const handleDisconnectIntegration = async (integrationKey) => {
    try {
      if (!currentUser) {
        setMessage({
          type: 'error',
          text: 'Please log in to disconnect integrations.'
        });
        return;
      }

      // Account-level integrations for portal clients
      const accountLevelIntegrations = ['quickbooks', 'docusign', 'googleanalytics'];
      if (accountLevelIntegrations.includes(integrationKey) && accountId) {
        await disconnectAccountIntegrationConnection(accountId, integrationKey);
      } else if (integrationKey === 'fathom') {
        await disconnectFathom();
      } else {
        await disconnectUserIntegration(currentUser.uid, integrationKey);
      }
      // Refresh integrations status
      const userStatus = await getUserIntegrationsStatus(currentUser.uid);
      let merged = { ...userStatus };
      const acctLevelKeys = ['quickbooks', 'docusign', 'googleanalytics'];
      if (acctLevelKeys.includes(integrationKey) && accountId) {
        merged = { ...merged, [integrationKey]: { connected: false } };
      } else if (accountId) {
        try {
          const accountStatus = await getAccountIntegrationsStatus(accountId, { forceRefresh: true });
          const acctConnected = accountStatus.connectedIntegrations || accountStatus;
          merged = {
            ...merged,
            quickbooks: acctConnected.quickbooks || { connected: false },
            docusign: acctConnected.docusign || { connected: false },
            googleanalytics: acctConnected.googleanalytics || { connected: false },
          };
        } catch (e) {
          merged = { ...merged, quickbooks: { connected: false }, docusign: { connected: false }, googleanalytics: { connected: false } };
        }
      }
      setIntegrationsStatus(merged);
      const isAccountLevelIntegration = acctLevelKeys.includes(integrationKey);
      const displayName = isAccountLevelIntegration ? getIntegrationDisplayName(integrationKey) : getUserIntegrationDisplayName(integrationKey);
      setMessage({
        type: 'success',
        text: `${displayName} disconnected successfully!`
      });
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      const isAccountLevelIntegration = integrationKey === 'quickbooks' || integrationKey === 'docusign';
      const displayName = isAccountLevelIntegration ? getIntegrationDisplayName(integrationKey) : getUserIntegrationDisplayName(integrationKey);
      setMessage({
        type: 'error',
        text: `Failed to disconnect ${displayName}: ${error.message}`
      });
    }
  };

  const handleSubmitFathomApiKey = async () => {
    if (!currentUser) return;
    try {
      setConnectingIntegration('fathom');
      await connectFathomWithApiKey(fathomApiKeyDraft);
      setShowFathomKeyModal(false);
      setFathomApiKeyDraft('');
      const userStatus = await getUserIntegrationsStatus(currentUser.uid);
      let merged = { ...userStatus };
      if (accountId) {
        try {
          const accountStatus = await getAccountIntegrationsStatus(accountId, { forceRefresh: true });
          merged = { ...merged, quickbooks: accountStatus.quickbooks || merged.quickbooks };
        } catch (_) {
          /* ignore */
        }
      }
      setIntegrationsStatus(merged);
      setMessage({
        type: 'success',
        text: 'Fathom connected. Meeting summaries will sync when recordings finish.',
      });
    } catch (error) {
      console.error('Fathom connect failed:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to connect Fathom',
      });
    } finally {
      setConnectingIntegration(null);
    }
  };

  const handleNotificationToggle = async (notificationKey) => {
    const newValue = !notifications[notificationKey];
    
    // Update local state immediately for UI responsiveness
    const updatedNotifications = {
      ...notifications,
      [notificationKey]: newValue
    };
    
    setNotifications(updatedNotifications);

    // Save to Firestore
    try {
      if (!currentUser) {
        setMessage({
          type: 'error',
          text: 'Please log in to update notification preferences.'
        });
        return;
      }

      const collectionName = isInternal ? 'internal_users' : 'users';
      await saveUserNotificationPreferencesForCollection(currentUser.uid, updatedNotifications, collectionName);
      
      setMessage({
        type: 'success',
        text: `${getNotificationDisplayName(notificationKey)} notification ${newValue ? 'enabled' : 'disabled'} successfully!`
      });
    } catch (error) {
      console.error('Error saving notification preference:', error);
      setMessage({
        type: 'error',
        text: `Failed to save ${getNotificationDisplayName(notificationKey)} preference. Please try again.`
      });
      
      // Revert the local state on error
      setNotifications({
        ...notifications,
        [notificationKey]: !newValue
      });
    }
  };


  // Deep links like /portal/profile#integrations land on the right section
  // (the target only exists once the page content has rendered).
  useEffect(() => {
    if (loading || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  if (loading) {
    return (
      <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
        {/* Same ambient BBA background the Start profile shows */}
        <PortalBackground />
        <div className="relative max-w-4xl mx-auto w-full">
          <div className="text-center mb-6 animate-pulse">
            <div className="h-7 w-40 mx-auto rounded bg-gray-100 dark:bg-gray-800"></div>
            <div className="h-3 w-64 mx-auto mt-2 rounded bg-gray-100 dark:bg-gray-800"></div>
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 mb-6 animate-pulse">
              <div className="h-4 w-44 rounded bg-gray-100 dark:bg-gray-800 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800"></div>
                    <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
      {/* Same ambient BBA background the Start profile shows */}
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="relative mb-6">
          <div className="text-center px-14 md:px-32 py-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage your personal info, preferences, and integrations
            </p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl border text-sm font-medium ${
            message.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Profile Information */}
        <div className={sectionCard}>
          <SectionHeading emoji="👤">
            Personal Information
          </SectionHeading>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div
                className="relative w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer ring-2 ring-gray-200 dark:ring-gray-700 hover:ring-red-300 dark:hover:ring-red-700 transition-all group"
                onClick={handleImageClick}
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-full h-full text-gray-300 dark:text-gray-600 p-4" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Profile Picture</span>
              {profileImage && (
                <button
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                  onClick={handleDeleteImage}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className={labelBase}>
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={profileData.firstName}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your first name"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className={labelBase}>
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={profileData.lastName}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your last name"
              />
            </div>

            {/* Phone */}
            <div>
              <label className={labelBase}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your phone number"
              />
            </div>

            {/* Email */}
            <div>
              <label className={labelBase}>
                Email Address
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  disabled
                  placeholder="Email address"
                  className="flex-1 min-w-0 px-3 py-2.5 bg-transparent border-0 rounded-lg text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed focus:ring-0 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowEmailModal(true)}
                  className="text-sm font-medium px-3 py-2 rounded-lg shrink-0 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Change
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className={sectionCard}>
          <SectionHeading emoji="🏢">
            Company Information
          </SectionHeading>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
            {/* Company Logo */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div
                className="relative w-24 h-24 rounded-xl bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer ring-2 ring-gray-200 dark:ring-gray-700 hover:ring-red-300 dark:hover:ring-red-700 transition-all group flex items-center justify-center"
                onClick={handleCompanyLogoClick}
              >
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Building2 className="w-full h-full text-gray-300 dark:text-gray-600 p-6" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              <input
                type="file"
                ref={companyLogoInputRef}
                onChange={handleCompanyLogoChange}
                accept="image/*"
                className="hidden"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Company Logo</span>
              {companyLogo && (
                <button
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                  onClick={handleDeleteCompanyLogo}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>

            {/* Company Name + Website + Colors */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelBase}>
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={profileData.company}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your company name"
                />
              </div>
              <div>
                <label className={labelBase}>
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={profileData.website}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="https://yourcompany.com"
                />
              </div>
            {/* Primary Color */}
            <div>
              <label className={labelBase}>
                Primary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={profileData.companyColors.primary}
                  onChange={(e) => {
                    setProfileData(prev => ({
                      ...prev,
                      companyColors: {
                        ...prev.companyColors,
                        primary: e.target.value
                      }
                    }));
                  }}
                  className="w-14 h-10 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer bg-transparent p-1"
                />
                <input
                  type="text"
                  value={profileData.companyColors.primary}
                  onChange={(e) => {
                    setProfileData(prev => ({
                      ...prev,
                      companyColors: {
                        ...prev.companyColors,
                        primary: e.target.value
                      }
                    }));
                  }}
                  className={`flex-1 ${inputBase}`}
                  placeholder="#dc2626"
                />
              </div>
            </div>

            {/* Secondary Color */}
            <div>
              <label className={labelBase}>
                Secondary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={profileData.companyColors.secondary}
                  onChange={(e) => {
                    setProfileData(prev => ({
                      ...prev,
                      companyColors: {
                        ...prev.companyColors,
                        secondary: e.target.value
                      }
                    }));
                  }}
                  className="w-14 h-10 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer bg-transparent p-1"
                />
                <input
                  type="text"
                  value={profileData.companyColors.secondary}
                  onChange={(e) => {
                    setProfileData(prev => ({
                      ...prev,
                      companyColors: {
                        ...prev.companyColors,
                        secondary: e.target.value
                      }
                    }));
                  }}
                  className={`flex-1 ${inputBase}`}
                  placeholder="#1f2937"
                />
              </div>
            </div>
          </div>

          </div>

          {/* Address Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Street Address */}
            <div className="md:col-span-2">
              <label className={labelBase}>
                Street Address
              </label>
              <input
                type="text"
                name="address.street"
                value={profileData.address.street}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your street address"
              />
            </div>

            {/* City */}
            <div>
              <label className={labelBase}>
                City
              </label>
              <input
                type="text"
                name="address.city"
                value={profileData.address.city}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your city"
              />
            </div>

            {/* State */}
            <div>
              <label className={labelBase}>
                State/Province
              </label>
              <input
                type="text"
                name="address.state"
                value={profileData.address.state}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your state or province"
              />
            </div>

            {/* Zip Code */}
            <div>
              <label className={labelBase}>
                ZIP/Postal Code
              </label>
              <input
                type="text"
                name="address.zipCode"
                value={profileData.address.zipCode}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your ZIP or postal code"
              />
            </div>

            {/* Country */}
            <div>
              <label className={labelBase}>
                Country
              </label>
              <select
                name="address.country"
                value={profileData.address.country}
                onChange={handleInputChange}
                className={inputBase}
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* My Team - only show if they have other team members */}
          {!isInternal && teamMembers.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <span className="text-base leading-none">👥</span>
                My Team
              </p>
              <div className="flex flex-wrap gap-2">
                {teamMembers.map((member) => (
                  <span
                    key={member.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800"
                  >
                    <User className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                    {member.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Style Preferences */}
        <div className={sectionCard}>
          <SectionHeading emoji="🎨">
            Style Preferences
          </SectionHeading>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-lg shrink-0">
                🌙
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Dark Mode</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Switch the portal to a darker color palette</p>
              </div>
            </div>
            <Toggle checked={darkMode} onChange={toggleDarkMode} ariaLabel="Toggle dark mode" />
          </div>
        </div>


        {/* Billing Section */}
        <div className={sectionCard}>
          <SectionHeading emoji="💳">
            Billing &amp; Subscription
          </SectionHeading>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 shadow-sm">
                <CreditCard className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Billing &amp; Payment Methods</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Subscription, invoices, and ACH details</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/portal/billing')}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors shrink-0"
            >
              <CreditCard className="w-4 h-4" />
              Manage Billing
            </button>
          </div>
        </div>

        {/* Notifications Section */}
        <div className={sectionCard}>
          <SectionHeading emoji="🔔">
            Notifications
          </SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {NOTIFICATION_TILES.map(({ key, label, emoji, tile }) => {
              const enabled = notifications[key];
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg ${tile}`}>
                      {emoji}
                    </div>
                    <p className={`text-sm font-medium truncate ${
                      enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {label}
                    </p>
                  </div>
                  <Toggle
                    checked={enabled}
                    onChange={() => handleNotificationToggle(key)}
                    ariaLabel={`Toggle ${label} notifications`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Integrations Section */}
        <div id="integrations" className={`${sectionCard} scroll-mt-24`}>
          <SectionHeading emoji="🔌">
            Integrations
          </SectionHeading>
          <div className="space-y-3">
            {/* QuickBooks */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-4 min-w-0">
                <img src="/qbo.png" alt="QuickBooks" className="w-8 h-8 shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">QuickBooks</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your QuickBooks account to sync financial data</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {integrationsStatus.quickbooks?.connected ? (
                  <>
                    <button
                      onClick={() => handleDisconnectIntegration('quickbooks')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Disconnect
                    </button>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Connected
                    </span>
                  </>
                ) : !isIntegrationConfigured('quickbooks') || !accountId ? (
                  <button
                    disabled={true}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  >
                    Coming Soon
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnectIntegration('quickbooks')}
                    disabled={connectingIntegration === 'quickbooks'}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                  >
                    {connectingIntegration === 'quickbooks' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* DocuSign — internal users only */}
            {isInternal && <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-4 min-w-0">
                <img src="/docusignlogo.png" alt="DocuSign" className="w-8 h-8 object-contain shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">DocuSign</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your DocuSign account for NDA signature workflows</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {integrationsStatus.docusign?.connected ? (
                  <>
                    <button
                      onClick={() => handleDisconnectIntegration('docusign')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Disconnect
                    </button>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Connected
                    </span>
                  </>
                ) : !isIntegrationConfigured('docusign') || !accountId ? (
                  <button
                    disabled={true}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  >
                    Coming Soon
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnectIntegration('docusign')}
                    disabled={connectingIntegration === 'docusign'}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                  >
                    {connectingIntegration === 'docusign' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                )}
              </div>
            </div>}

            {/* Google Analytics */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-4 min-w-0">
                <img src="/gdata-icon.svg" alt="Google Analytics" className="w-8 h-8 object-contain shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Google Analytics</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {integrationsStatus.googleanalytics?.authData?.propertyDisplayName
                      ? `Connected — ${integrationsStatus.googleanalytics.authData.propertyDisplayName}`
                      : 'Connect to view your website analytics in the portal'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {integrationsStatus.googleanalytics?.connected ? (
                  <>
                    <button
                      onClick={() => handleDisconnectIntegration('googleanalytics')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Disconnect
                    </button>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Connected
                    </span>
                  </>
                ) : !isIntegrationConfigured('googleanalytics') || !accountId ? (
                  <button
                    disabled
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  >
                    Not Available
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnectIntegration('googleanalytics')}
                    disabled={connectingIntegration === 'googleanalytics'}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                  >
                    {connectingIntegration === 'googleanalytics' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* JobTread */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-4 min-w-0">
                <img src="/jtreadlogo.png" alt="JobTread" className="w-8 h-8 shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">JobTread</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your JobTread account using your Grant Key from Grant Management</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {integrationsStatus.jobtread?.connected ? (
                  <>
                    <button
                      onClick={() => handleDisconnectIntegration('jobtread')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Disconnect
                    </button>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Connected
                    </span>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnectIntegration('jobtread')}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>

            {isInternal && (
              <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-4 min-w-0">
                  <img src="/fathomlogo.png" alt="Fathom" className="w-8 h-8 object-contain shrink-0" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Fathom</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Use Fathom <strong className="font-medium">API Access</strong> → API key (not MCP Server). Syncs meeting summaries and client tasks.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {integrationsStatus.fathom?.connected ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleDisconnectIntegration('fathom')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        Disconnect
                      </button>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Connected
                      </span>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleConnectIntegration('fathom')}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {showFathomKeyModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowFathomKeyModal(false); setFathomApiKeyDraft(''); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowFathomKeyModal(false); setFathomApiKeyDraft(''); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mb-2">
                    <Plug className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Connect Fathom</h2>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  In Fathom open <strong className="font-medium text-gray-900 dark:text-white">API Access</strong> and create an API key. Do not use <strong className="font-medium">MCP Server</strong> for this connection. Keys stay on the server; a webhook is registered for your recordings.
                </p>
                <div className="mb-5">
                  <label className={labelBase}>
                    API Key
                  </label>
                  <input
                    type="password"
                    autoComplete="off"
                    value={fathomApiKeyDraft}
                    onChange={(e) => setFathomApiKeyDraft(e.target.value)}
                    placeholder="Fathom API key"
                    className={inputBase}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFathomKeyModal(false);
                      setFathomApiKeyDraft('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!fathomApiKeyDraft.trim() || connectingIntegration === 'fathom'}
                    onClick={handleSubmitFathomApiKey}
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                  >
                    Save &amp; register webhook
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* JobTread Test Component - Only show when JobTread is connected and in dev mode */}
        {isDev && integrationsStatus.jobtread?.connected && (
          <div className={sectionCard}>
            <SectionHeading emoji="🧪">
              JobTread API Test
            </SectionHeading>
            <JobTreadTest darkMode={darkMode} />
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Email Change Modal */}
        {showEmailModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-2">
                    <Mail className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email address change</h2>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  For security, when you change your email address, we'll send a message to your current email so you can review the change.
                </p>
                {emailModalError && (
                  <div className="mb-4 p-3 rounded-xl border text-sm bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800">
                    {emailModalError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className={labelBase}>
                      New email address
                    </label>
                    <input
                      type="email"
                      value={emailChangeData.newEmail}
                      onChange={(e) => setEmailChangeData(prev => ({ ...prev, newEmail: e.target.value }))}
                      placeholder="you@example.com"
                      className={inputBase}
                    />
                  </div>
                  <div>
                    <label className={labelBase}>
                      Current password
                    </label>
                    <div className="relative">
                      <input
                        type={emailPasswordVisible ? 'text' : 'password'}
                        value={emailChangeData.password}
                        onChange={(e) => setEmailChangeData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter your password to confirm"
                        className={`${inputBase} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setEmailPasswordVisible(prev => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label={emailPasswordVisible ? 'Hide password' : 'Show password'}
                      >
                        {emailPasswordVisible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailChange}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Updating...' : 'Update email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowPasswordModal(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowPasswordModal(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-5">
                <form onSubmit={handlePasswordChange}>
                  <div className="mb-4">
                    <label className={labelBase}>
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordInputChange}
                      required
                      className={inputBase}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="mb-6">
                    <label className={labelBase}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      required
                      className={inputBase}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setPasswordData({ newPassword: '', confirmPassword: '' });
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors"
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* JobTread Grant Key Modal */}
        {showJobTreadModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowJobTreadModal(false); setJobTreadGrantKey(''); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowJobTreadModal(false); setJobTreadGrantKey(''); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mb-2">
                    <Plug className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Connect JobTread</h2>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Please enter your JobTread Grant Key. You can find this in your JobTread account under Grant Management.
                </p>
                <div className="mb-6">
                  <label className={labelBase}>
                    Grant Key
                  </label>
                  <input
                    type="text"
                    value={jobTreadGrantKey}
                    onChange={(e) => setJobTreadGrantKey(e.target.value)}
                    className={inputBase}
                    placeholder="Enter your JobTread Grant Key"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleJobTreadSubmit();
                      }
                    }}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowJobTreadModal(false);
                      setJobTreadGrantKey('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleJobTreadSubmit}
                    disabled={connectingIntegration === 'jobtread'}
                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                  >
                    {connectingIntegration === 'jobtread' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;